import React, { useState, useRef, useEffect } from 'react';
import { Editor } from '@tinymce/tinymce-react';

const AddProductModal = ({ 
  newProduct, 
  setNewProduct, 
  categories,
  onSubmit,
  onImageUpload
}) => {
  const [previewImages, setPreviewImages] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const editorRef = useRef(null);

  // Initialize price table with preset durations when component mounts
  useEffect(() => {
    if (newProduct.price.length === 0) {
      const presetDurations = [4, 8, 12, 16, 20, 24, 30];
      const initialPrices = presetDurations.map(duration => ({
        nb_days: duration,
        price: 0
      }));
      setNewProduct({
        ...newProduct,
        price: initialPrices
      });
    }
  }, []);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Create preview URLs for selected images
    const previews = files.map(file => ({
      url: URL.createObjectURL(file),
      file: file
    }));
    
    setPreviewImages([...previewImages, ...previews]);
    setSelectedFiles([...selectedFiles, ...files]);
  };

  const handleImageDelete = (index) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette image ?')) {
      // Remove preview image
      const updatedPreviews = previewImages.filter((_, i) => i !== index);
      const updatedFiles = selectedFiles.filter((_, i) => i !== index);
      setPreviewImages(updatedPreviews);
      setSelectedFiles(updatedFiles);
      
      // Cleanup preview URL
      URL.revokeObjectURL(previewImages[index].url);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const productData = { 
      ...newProduct,
      files: selectedFiles,
      description: editorRef.current ? editorRef.current.getContent() : ''
    };
    
    try {
      await onSubmit(e, productData);
      // Reset image previews and selected files after successful submission
      previewImages.forEach(preview => URL.revokeObjectURL(preview.url));
      setPreviewImages([]);
      setSelectedFiles([]);
    } catch (error) {
      console.error('Error submitting product:', error);
    }
  };

  return (
    <div className="modal" id="addProductModal" tabIndex="-1">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header">
              <h5 className="modal-title">Ajouter un produit</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div className="modal-body">
              {/* Image Upload Section */}
              <div className="mb-4">
                <label className="form-label">Images du produit</label>
                <div className="d-flex flex-wrap gap-3 mb-3">
                  {/* Preview Images */}
                  {previewImages.map((preview, index) => (
                    <div key={index} className="position-relative" style={{ width: '150px', height: '150px' }}>
                      <img
                        src={preview.url}
                        alt={`Preview ${index + 1}`}
                        className="img-thumbnail"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <button
                        type="button"
                        className="btn btn-danger btn-sm position-absolute top-0 end-0"
                        onClick={() => handleImageDelete(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                
                <div className="mb-3">
                  <input
                    type="file"
                    className="form-control"
                    multiple
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                  <small className="text-muted">
                    Vous pouvez sélectionner plusieurs images à la fois
                  </small>
                </div>
              </div>

              {/* Product Details */}
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Titre</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newProduct.titre}
                      onChange={(e) => setNewProduct({...newProduct, titre: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Référence</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newProduct.reference}
                      onChange={(e) => setNewProduct({...newProduct, reference: e.target.value})}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Description</label>
                <Editor
                  onInit={(evt, editor) => editorRef.current = editor}
                  init={{
                    height: 300,
                    menubar: false,
                    plugins: [
                      'lists',
                      'autolink',
                      'autoresize',
                      'help',
                      'wordcount'
                    ],
                    toolbar: 'bold italic underline | ' +
                            'bullist numlist | ' +
                            'removeformat help',
                    content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                  }}
                  onEditorChange={(content) => {
                    setNewProduct(prev => ({...prev, description: content}));
                  }}
                />
              </div>

              {/* Rest of your form remains the same */}
              <div className="row">
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Prix journalier (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      value={newProduct.daily_price}
                      onChange={(e) => setNewProduct({...newProduct, daily_price: parseFloat(e.target.value)})}
                      required
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Caution (€)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={newProduct.caution}
                      onChange={(e) => setNewProduct({...newProduct, caution: parseFloat(e.target.value)})}
                      required
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Quantité</label>
                    <input
                      type="number"
                      className="form-control"
                      value={newProduct.quantity}
                      onChange={(e) => setNewProduct({...newProduct, quantity: parseInt(e.target.value)})}
                      required
                      min="0"
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Catégorie</label>
                    <select
                      className="form-select"
                      value={newProduct.category}
                      onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}
                      required
                    >
                      <option value="">Sélectionner une catégorie</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Disponibilité</label>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={newProduct.available}
                        onChange={(e) => setNewProduct({...newProduct, available: e.target.checked})}
                      />
                      <label className="form-check-label">
                        {newProduct.available ? 'Disponible' : 'Indisponible'}
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Price Table */}
              <div className="mb-3">
                <label className="form-label">Tarifs par durée</label>
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Durée (jours)</th>
                      <th>Prix (€)</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {newProduct.price.map((price, index) => (
                      <tr key={index}>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={price.nb_days}
                            onChange={(e) => {
                              const updatedPrices = [...newProduct.price];
                              updatedPrices[index].nb_days = parseInt(e.target.value);
                              setNewProduct({...newProduct, price: updatedPrices});
                            }}
                            min="1"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={price.price}
                            onChange={(e) => {
                              const updatedPrices = [...newProduct.price];
                              updatedPrices[index].price = parseFloat(e.target.value);
                              setNewProduct({...newProduct, price: updatedPrices});
                            }}
                            step="0.01"
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => {
                              const updatedPrices = newProduct.price.filter((_, i) => i !== index);
                              setNewProduct({...newProduct, price: updatedPrices});
                            }}
                          >
                            X
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="3">
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={() => {
                            setNewProduct({
                              ...newProduct,
                              price: [...newProduct.price, { nb_days: 0, price: 0 }]
                            });
                          }}
                        >
                          Ajouter un tarif
                        </button>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                Annuler
              </button>
              <button type="submit" className="btn btn-primary">
                Ajouter
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddProductModal;